import { config } from '../../../config';
import DigiobcanApi, { type ReportStatus } from '../../api/DigiobcanApi';
import type { FeedMedia, IFeedItem } from '../../components/FeedItem/FeedItem';
import { createQueryFn, QueryNameSpace } from '../../lib/query';
import type { QueryFunctionContext } from '@tanstack/react-query';

/**
 * Prílohy podnetu do tvaru, s akým pracuje feed aj detail v paneli.
 *
 * Nesú sa **celé**, nie len prvý obrázok: detail z nich kreslí pás náhľadov
 * a galériu, takže by si inak musel dotiahnuť podnet druhýkrát len kvôli
 * druhej fotke.
 */
const toFeedMedia = (
  media: { public_url: string; media_type: string; filename: string }[],
): FeedMedia[] =>
  media.map((item) => ({
    url: item.public_url,
    type: item.media_type,
    filename: item.filename,
  }));

/**
 * Stav podnetu zo stavov jeho nálezov - **to isté pravidlo, aké má
 * administrácia** v `deriveReportStatus` na strane API.
 *
 * Verejný `GET /reports` je per-nález, nie per-podnet: podnet s dvoma nálezmi
 * (napr. „Životné prostredie" prijaté a „Ostatné / Nezaradené" ešte nie) príde
 * v odpovedi dvakrát a `location`/`votes` sa v oboch riadkoch zhodujú. Kým sa
 * druhý riadok ticho zahadzoval, o stave rozhodlo poradie z SQL
 * (`ORDER BY i.issue_id DESC` nad náhodnými identifikátormi), takže občan videl
 * náhodný z tých dvoch stavov: podnet, ktorý má úradník na stole, sa na mape
 * hlásil ako „Nové" a bunka „V riešení" počítala menej, než koľko ich
 * administrácia ukazuje.
 *
 * **Prijatý nález prebíja všetko ostatné** - na podnete sa robí. Mimo toho
 * vyhráva najmenej pokročilý stav, takže podnet s jedným vyriešeným a jedným
 * neprideleným nálezom zostáva „Nové" a nezakryje, že ešte niečo potrebuje.
 */
const ISSUE_STATUS_PROGRESS: Record<string, number> = {
  open: 0,
  accepted: 1,
  duplicate: 2,
  resolved: 3,
};

const mergeReportStatus = (
  current: ReportStatus | undefined,
  next: ReportStatus | undefined,
): ReportStatus | undefined => {
  if (!current) return next;
  if (!next) return current;
  if (current === 'accepted' || next === 'accepted') return 'accepted';

  const currentRank = ISSUE_STATUS_PROGRESS[current];
  const nextRank = ISSUE_STATUS_PROGRESS[next];
  if (currentRank === undefined) return next;
  if (nextRank === undefined) return current;

  return nextRank < currentRank ? next : current;
};

type Params = {
  location?: string;
  category?: string[];
  orderBy?: 'date' | 'location' | 'votes';
  orderDirection?: 'asc' | 'desc';
  filter?: {
    news?: boolean;
    publicReports?: boolean;
    userReports?: boolean;
  };
  isPublic?: boolean;
};

export const getFeed = createQueryFn(
  QueryNameSpace.getFeed,
  async ({
    queryKey,
    pageParam,
  }: QueryFunctionContext<
    [string] | [string, Params],
    {
      news?: string[];
      userReports?: string[];
      publicReports?: number;
    }
  >) => {
    const [, params] = queryKey;

    const { location, category, orderBy, orderDirection, filter, isPublic } =
      params || {};

    const fetchNews =
      filter?.news && (pageParam?.news !== null || !pageParam)
        ? // Bez `location`: s ňou API radí podľa vzdialenosti, a keďže novinky
          // polohu väčšinou nemajú, skončili v poradí podľa ID, nie dátumu.
          // Novinky sú vždy od najnovšej.
          DigiobcanApi.getNews({
            limit: config.CUSTOM_LIMIT.FEED.NEWS,
            ...(!!category?.length && {
              category: category.map((cat) => cat.replace('cat:', '')),
            }),
            pagecursor: pageParam?.news,
          })
        : Promise.resolve({ news: [], nextCursor: null });

    const fetchUserReports =
      filter?.userReports &&
      !isPublic &&
      (pageParam?.userReports !== null || !pageParam)
        ? DigiobcanApi.meGetReports({
            location,
            orderBy: orderBy || 'location',
            orderDirection: orderDirection || 'asc',
            limit: config.CUSTOM_LIMIT.FEED.USER_REPORTS,
            offset: pageParam?.userReports
              ? Number(pageParam.userReports)
              : undefined,
          })
        : Promise.resolve({ reports: [], hasNextOffset: false, offset: null });

    const fetchPublicReports =
      filter?.publicReports && (pageParam?.publicReports !== null || !pageParam)
        ? DigiobcanApi.getReports({
            location,
            orderBy: orderBy || 'location',
            orderDirection: orderDirection || 'asc',
            limit: config.CUSTOM_LIMIT.FEED.PUBLIC_REPORTS,
            offset: pageParam?.publicReports
              ? Number(pageParam?.publicReports)
              : undefined,
          })
        : Promise.resolve({
            reports: [],
            offset: null,
            hasNextOffset: false,
          });

    const [newsResponse, userReportsResponse, publicReportsResponse] =
      await Promise.all([fetchNews, fetchUserReports, fetchPublicReports]);

    const uniqueItemsMap = new Map<string, IFeedItem>();

    if (filter?.news !== false) {
      const newsData = newsResponse.news || [];
      newsData.forEach((item) => {
        if (!uniqueItemsMap.has(item.id)) {
          uniqueItemsMap.set(item.id, {
            type: 'news',
            id: item.id,
            title: item.title,
            date: item.date,
            location: item.location as {
              type: 'Point';
              coordinates: [number, number];
            },
            image: item.image,
            description: item.description,
            chips: item.categories?.map((category) => ({
              label: category.name,
            })),
          });
        }
      });
    }

    if (filter?.publicReports) {
      const reportsData = publicReportsResponse.reports || [];
      reportsData.forEach((report) => {
        // Druhý (tretí, ...) nález toho istého podnetu. Zo stavov sa skladá
        // stav podnetu - viď `mergeReportStatus`; zvyšok riadku je v oboch
        // rovnaký, takže sa nemá čo dopĺňať.
        const existing = uniqueItemsMap.get(report.id);
        if (existing) {
          uniqueItemsMap.set(report.id, {
            ...existing,
            status: mergeReportStatus(existing.status, report.status),
          });
          return;
        }

        const imageMedia = report.media.find((m) =>
          m.media_type.startsWith('image'),
        );
        const videoMedia = report.media.find((m) =>
          m.media_type.startsWith('video'),
        );
        uniqueItemsMap.set(report.id, {
          type: 'report',
          id: report.id,
          title: report.title,
          date: report.inserted_at,
          location: report.location as {
            type: 'Point';
            coordinates: [number, number];
          },
          image: imageMedia?.public_url,
          video: !imageMedia ? videoMedia?.public_url : undefined,
          media: toFeedMedia(report.media),
          description: report.description,
          votesCount: report.votes_count || 0,
          status: report.status,
        });
      });
    }

    if (filter?.userReports) {
      const userReportsData = userReportsResponse.reports || [];
      userReportsData.forEach((report) => {
        // Vlastný podnet je spravidla aj vo verejnom zozname, takže v mape už
        // leží ako `report`. Kým sa tu preskakoval, bunka „Moje" v paneli ho
        // nezapočítala - vypadli z nej presne tie podnety, ktoré úrad prijal
        // alebo vyriešil, teda tie, na ktorých občanovi záleží najviac.
        // Nahrádza sa preto **typ** (podnet je môj a mapa ho tak má aj
        // označiť), zvyšok ostáva z verejných dát: `/me/reports` pozná len
        // `accepted`, takže by stav `resolved` prepísal na „v riešení".
        const existing = uniqueItemsMap.get(report.id);

        if (existing) {
          uniqueItemsMap.set(report.id, {
            ...existing,
            type: 'userReport',
          });
          return;
        }

        const imageMedia = report.media.find((m) =>
          m.media_type.startsWith('image'),
        );
        const videoMedia = report.media.find((m) =>
          m.media_type.startsWith('video'),
        );
        uniqueItemsMap.set(report.id, {
          type: 'userReport',
          id: report.id,
          title: report.title,
          description: report.description,
          date: report.inserted_at,
          image: imageMedia?.public_url,
          video: !imageMedia ? videoMedia?.public_url : undefined,
          media: toFeedMedia(report.media),
          location: report.location as {
            type: 'Point';
            coordinates: [number, number];
          },
          votesCount: report.votes_count || 0,
          // `/me/reports` stav neposiela, len `accepted` - vlastný podnet
          // teda vie byť „podané" alebo „v riešení", nie „vyriešené".
          // Do štatistiky v paneli vstupuje hlavne cez bunku „Moje", ktorá
          // ide podľa typu, nie podľa stavu.
          status: report.accepted ? 'accepted' : 'open',
        });
      });
    }

    // Vytvoríme objekt s dátami a nextCursors
    const result = {
      items: Array.from(uniqueItemsMap.values()),
      nextCursors: {
        news: newsResponse.nextCursor || null,
        userReports: userReportsResponse.hasNextOffset
          ? userReportsResponse.offset + config.CUSTOM_LIMIT.FEED.USER_REPORTS
          : null,
        publicReports: publicReportsResponse.hasNextOffset
          ? publicReportsResponse.offset +
            config.CUSTOM_LIMIT.FEED.PUBLIC_REPORTS
          : null,
      },
    };

    return result;
  },
);
